import { UploadProps, message } from "antd";

export function publicImagePath(image: string): string {
  return process.env.PUBLIC_URL + `/img/${image}`;
}

export function publicDocsPath(document: string): string {
  return process.env.PUBLIC_URL + `/documents/${document}`;
}

export function uploadedImagePath(image: string): string {
  if (!image) return "";

  // Handle legacy full S3 URLs (for backward compatibility)
  if (image.startsWith("https://")) {
    // Extract just the filename from the S3 URL
    const parts = image.split("/");
    const filename = parts[parts.length - 1];
    // Route through server to get signed URL
    return process.env.PUBLIC_URL + `/uploaded_images/${filename}`;
  }

  // All images are now served through the server
  // The server will generate signed URLs for S3 or serve local files
  return process.env.PUBLIC_URL + `/uploaded_images/${image}`;
}

/**
 * Like uploadedImagePath, but appends a `?w=` query param so the proxy
 * returns a sharp-resized JPEG (≤2048px, the ladder ceiling) instead of the
 * full original.
 */
export function uploadedImageThumbPath(image: string, width: number): string {
  const base = uploadedImagePath(image);
  if (!base) return "";
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}w=${width}`;
}

// `Quote.fileList` is the MIXED-MEDIA lane — the funnel, the patient app,
// SMS/MMS and the partner API all write into it, and staff move the odd video
// there too — so not every key is an image. The /uploaded_images route above
// sharp-resizes IMAGE bytes and can't touch a video, so rendering a non-image
// key as an <img> yields a permanently broken tile. Every surface that renders
// fileList keys shares these predicates rather than re-deriving them (they live
// here, next to the key→URL helpers, so the patient portal doesn't have to
// import an admin component to ask a question about a key).
//
// Allow-list rather than a video deny-list: an unknown extension is better
// shown as a file tile (which always works) than as an image that might not
// load.
const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "heic",
  "heif",
  "avif",
]);

// Extensions worth a play affordance rather than a generic file icon.
const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "m4v",
  "webm",
  "avi",
  "mkv",
  "3gp",
]);

/** Lowercased extension of an S3 key, or null when it has none. */
export function keyExtension(url: string): string | null {
  const path = (url ?? "").split(/[?#]/)[0];
  const base = path.split("/").pop() ?? "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return null;
  return base.slice(dot + 1).toLowerCase();
}

/**
 * Can this key be rendered as an image? An extension-less key counts as one:
 * the oldest funnel uploads predate extension-preserving keys and are all JPEGs.
 */
export function isImageKey(url: string): boolean {
  const ext = keyExtension(url);
  return ext === null || IMAGE_EXTENSIONS.has(ext);
}

/** Is this key a video we should show a play affordance for? */
export function isVideoKey(url: string): boolean {
  const ext = keyExtension(url);
  return ext !== null && VIDEO_EXTENSIONS.has(ext);
}

/**
 * Streaming URL for a procedure video episode (Procedure.videos[].videoKey).
 *
 * Resolves the app's base path exactly like uploadedImagePath, but the URL IS
 * the S3 key: every videoKey already starts with "procedure-videos/", and the
 * delivery route (server/routes/procedure-videos.ts) matches
 * `/^\/procedure-videos\/(.+)$/` and re-attaches that prefix to build the key.
 * So nothing extra is prepended here — `<base>/<videoKey>` and no more.
 */
export function procedureVideoUrl(videoKey: string): string {
  if (!videoKey) return "";
  return process.env.PUBLIC_URL + `/${videoKey}`;
}

// Pixel width used by the SalesViewV2 photo column. Cells are ~24px tall
// × up to 3 thumbs in a 140px-wide column, so the long edge is ≤48 device
// pixels at 2x DPR; 96 gives Retina headroom while keeping payload small.
export const SALES_GRID_THUMB_WIDTH = 96;

export function uploadedS3Path(key: string): string {
  return process.env.PUBLIC_URL + `/s3/get-s3-direct/?key=${key}`;
}

/**
 * When we are ready to upload images to S3, we will use this function.
 * @param setUrl
 * @returns
 */
// callback receives (s3 key, original filename) — the filename arg
// lets callers preserve `File.name` end-to-end into Quote.fileList so
// patient-uploaded filenames survive the GraphQL round-trip. The arg
// is positional + optional so existing call sites that only consume
// the key are unaffected.
export const uploadPropsS3 = (
  callback: (key: string, name?: string) => void,
) => {
  const props: UploadProps = {
    action: process.env.PUBLIC_URL + "/s3/upload-s3-direct",
    listType: "picture",
    onChange(info) {
      if (info.file.status === "done") {
        callback(info.file.response, info.file.name);
      }
    },
  };

  return props;
};

// Patients photograph a printed lab report at least as often as they save a
// PDF — both labs-upload lanes accept a PDF or a photo (JPEG/PNG/HEIC/HEIF,
// the last two being what an iPhone camera actually sends). The server
// (labsUploadNormalizationService.ts) is the real gate; this `accept` list
// only steers the OS file picker / camera-vs-file prompt and is not itself a
// security boundary.
const LABS_UPLOAD_ACCEPT = ".pdf,.jpg,.jpeg,.png,.heic,.heif";

/**
 * Patient-portal "Upload your labs" flow (useUploadLabsModal.tsx). Unlike
 * uploadPropsS3 above — which posts to the shared, anonymous-permitted
 * `/s3/upload-s3-direct` funnel route with keys that carry no binding to any
 * patient/quote — this posts to the quote-scoped
 * `/api/patient-labs-upload/:quoteId` route (patient-labs-upload-routes.ts),
 * which mints keys as `labs-uploads/<quoteId>/<uuid>.pdf` (or `.jpg` for a
 * normalized photo) only after verifying the session's patient owns
 * `quoteId`. The `patientUploadLabsOrderPDF` mutation independently
 * re-validates that shape server-side (labsUploadKeyService.ts) — this
 * endpoint choice is what makes that validation satisfiable for a legitimate
 * upload in the first place.
 *
 * The route requires a real patient session — same JWT `relayEnvironment.ts`
 * already reads from `localStorage` and sends as `Authorization: Bearer` on
 * GraphQL calls, attached here as a plain header since antd's `Upload` posts
 * via its own XHR, not through the Relay network layer.
 *
 * The callback's second argument is the name the browser reported for the
 * picked file. It is DISPLAY METADATA for the admin document label only (Two
 * Lanes, 2026-08-12) — the key in the first argument is the one the server
 * minted, and nothing downstream derives a path or a lookup from the name.
 */
export const uploadPropsPatientLabsS3 = (
  quoteId: string,
  callback: (key: string, originalFilename?: string) => void,
) => {
  const token = localStorage.getItem("token");
  const props: UploadProps = {
    action: process.env.PUBLIC_URL + `/api/patient-labs-upload/${quoteId}`,
    listType: "picture",
    accept: LABS_UPLOAD_ACCEPT,
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    onChange(info) {
      if (info.file.status === "done") {
        callback(info.file.response, info.file.name);
      }
    },
  };

  return props;
};

/**
 * Coordinator-side "Upload results" lane (the case workspace Review tab's
 * admin upload lane — preop labs-intake PR3), for lab results that arrive by email/text instead
 * of through the patient's app or portal. Posts straight to the admin-gated
 * `POST /api/admin-labs-upload/:quoteId` (admin-labs-upload-routes.ts), which
 * mints the SAME `labs-uploads/<quoteId>/<uuid>.pdf` key shape as the patient
 * lane and runs the FULL pipeline server-side (record + fire-and-forget
 * classification) in one request — unlike uploadPropsPatientLabsS3, there is
 * no follow-up GraphQL mutation to call; the response IS the created record
 * (`{ key, patientSubmittedLabId }`). Auth rides the admin session's httpOnly
 * cookie automatically on this same-origin XHR — no bearer token needed,
 * unlike the patient lane below.
 *
 * `labsRequestId` pins the upload to a specific order (mirrors the optional
 * arg on `PatientUploadLabsOrderPDFInput`) — pass the case's active order id
 * so a follow-up-order upload never lands on a superseded one.
 */
export const uploadPropsAdminLabsS3 = (
  quoteId: string,
  labsRequestId: string | null,
  callback: () => void,
) => {
  const props: UploadProps = {
    action: process.env.PUBLIC_URL + `/api/admin-labs-upload/${quoteId}`,
    accept: LABS_UPLOAD_ACCEPT,
    showUploadList: false,
    data: labsRequestId ? { labsRequestId } : undefined,
    onChange(info) {
      if (info.file.status === "done") {
        callback();
      } else if (info.file.status === "error") {
        const reason =
          (info.file.response as { error?: string } | undefined)?.error ??
          "Upload failed.";
        message.error(reason);
      }
    },
  };

  return props;
};

export const uploadProps = (setImage: any) => {
  const props: UploadProps = {
    action: process.env.PUBLIC_URL + "/upload-image",
    listType: "picture",
    onChange(info) {
      if (info.file.status === "done") {
        // Handle response from API
        console.log(info.file.response);
        setImage(info.file.response.url);
      }
    },
  };

  return props;
};
